import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { countries } from "../../global/countriesAndSources";
import { getUserProfile } from "../../HTTP/user";
import {
  createCountryPurchase,
  getCountryPurchaseByUser,
  getPurchasedCountriesByUser,
  getCountryPurchasePrices,
  getPublicKeyStripe,
  selectCountryPurchaseCountries,
} from "../../HTTP/payment";
import {
  applyCountryPurchaseAccess,
  getAccessibleCountryCodesFromPurchase,
  getCountryPurchaseCountryCount,
  getCountryPurchaseId,
  getCountryPurchaseSelectedCodes,
  getCountryPurchaseStatus,
  getLatestCountryPurchase,
  getPurchasedCountryCodesFromResponse,
  getStoredAccessibleCountryCodes,
} from "../../Helper/countryPurchaseAccess";

const STATES = {
  loading: "loading",
  payment_required: "payment_required",
  payment_processing: "payment_processing",
  waiting_for_webhook: "waiting_for_webhook",
  country_selection: "country_selection",
  completed: "completed",
  error: "error",
};

const ALL_COUNTRIES_CODE = "wd";

const normalizeCurrency = (currency) => String(currency || "EUR").toUpperCase();

const getDisplayPrice = (price) => {
  const currency = normalizeCurrency(price?.currency || "EUR");

  const normalizeAmount = (value, isMinorUnitField = false) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return null;

    if (!isMinorUnitField) return numericValue;

    // Certaines APIs exposent unit_amount/amount en centimes (Stripe),
    // d'autres directement en euros. On évite d'afficher 0,05 € quand la valeur est 5 €.
    if (Number.isInteger(numericValue) && Math.abs(numericValue) >= 100) {
      return numericValue / 100;
    }
    return numericValue;
  };

  let amount = null;
  if (price?.display_price !== undefined && price?.display_price !== null) {
    amount = normalizeAmount(price.display_price, false);
  } else if (price?.price !== undefined && price?.price !== null) {
    amount = normalizeAmount(price.price, false);
  } else if (price?.unit_amount !== undefined && price?.unit_amount !== null) {
    amount = normalizeAmount(price.unit_amount, true);
  } else if (price?.amount !== undefined && price?.amount !== null) {
    amount = normalizeAmount(price.amount, true);
  } else if (price?.price_cents !== undefined && price?.price_cents !== null) {
    amount = normalizeAmount(price.price_cents, true);
  }

  if (!Number.isFinite(amount)) {
    return `${price?.price ?? price?.unit_amount ?? price?.amount ?? "-"} ${currency}`;
  }

  try {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch (error) {
    return `${amount.toFixed(2)} ${currency}`;
  }
};

const getClientSecretFromPurchase = (purchase) =>
  purchase?.client_secret || purchase?.clientSecret || purchase?.payment_intent_client_secret || "";

const getErrorMessage = (error, fallbackMessage) => {
  if (!error) return fallbackMessage;
  if (typeof error === "string") return error;
  if (error?.message) return error.message;
  return fallbackMessage;
};

const normalizeCountryCode = (value) => String(value || "").trim().toLowerCase();

const normalizeCountryCodeList = (values) => {
  if (!Array.isArray(values)) return [];
  const unique = [];
  for (const value of values) {
    const normalized = normalizeCountryCode(value);
    if (!normalized || unique.includes(normalized)) continue;
    unique.push(normalized);
  }
  return unique;
};

function CountryPaymentElement({
  clientSecret,
  stripePromise,
  disabled,
  onStart,
  onSuccess,
  onFailure,
}) {
  if (!clientSecret || !stripePromise) return null;

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        appearance: {
          theme: "stripe",
          variables: {
            colorPrimary: "#00739d",
          },
        },
      }}
    >
      <CountryPaymentForm
        clientSecret={clientSecret}
        disabled={disabled}
        onStart={onStart}
        onSuccess={onSuccess}
        onFailure={onFailure}
      />
    </Elements>
  );
}

function CountryPaymentForm({ clientSecret, disabled, onStart, onSuccess, onFailure }) {
  const stripe = useStripe();
  const elements = useElements();

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements || disabled) return;

    onStart();

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        onFailure(submitError.message || "Le formulaire de paiement est invalide.");
        return;
      }

      const result = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: `${window.location.origin}/purchase-countries`,
        },
        redirect: "if_required",
      });

      if (result.error) {
        onFailure(result.error.message || "Paiement refuse, merci de reessayer.");
        return;
      }

      onSuccess();
    } catch (error) {
      onFailure(getErrorMessage(error, "Erreur pendant le paiement."));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 mt-4">
      <PaymentElement />
      <button
        type="submit"
        disabled={disabled || !stripe || !elements}
        className="w-full py-3 px-4 rounded-lg text-white font-semibold bg-[#00739d] hover:bg-[#005d7f] disabled:bg-gray-400"
      >
        Confirmer le paiement
      </button>
    </form>
  );
}

export default function CountryPurchase() {
  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch (error) {
      return null;
    }
  }, []);

  const [status, setStatus] = useState(STATES.loading);
  const [prices, setPrices] = useState([]);
  const [selectedPriceId, setSelectedPriceId] = useState(null);
  const [stripeKey, setStripeKey] = useState("");
  const [purchase, setPurchase] = useState(null);
  const [clientSecret, setClientSecret] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedCountryCodes, setSelectedCountryCodes] = useState([]);
    const applyServerSelectedCodes = useCallback((codesFromApi) => {
      const normalizedFromApi = normalizeCountryCodeList(codesFromApi);
      setSelectedCountryCodes((previous) => {
        // Si l'API ne renvoie encore rien en statut paid, on préserve la sélection locale en cours.
        if (normalizedFromApi.length === 0 && previous.length > 0) {
          return previous;
        }
        return normalizedFromApi.filter((code) => code !== ALL_COUNTRIES_CODE);
      });
    }, []);

  const [countrySearchTerm, setCountrySearchTerm] = useState("");
  const [previouslyPurchasedCodes, setPreviouslyPurchasedCodes] = useState([]);
  const [isSubmittingSelection, setIsSubmittingSelection] = useState(false);

  const stripePromise = useMemo(() => (stripeKey ? loadStripe(stripeKey) : null), [stripeKey]);

  const selectedPrice = useMemo(
    () => prices.find((price) => Number(price.id) === Number(selectedPriceId)) || null,
    [prices, selectedPriceId]
  );

  const countryCount = useMemo(() => getCountryPurchaseCountryCount(purchase), [purchase]);

  const accessibleCodes = useMemo(() => {
    const stored = getStoredAccessibleCountryCodes(user?.country);
    const base = purchase
      ? getAccessibleCountryCodesFromPurchase(purchase, user?.country)
      : stored;
    const currentSelection =
      getCountryPurchaseStatus(purchase) === "paid" ? getCountryPurchaseSelectedCodes(purchase) : [];
    const previous = new Set(previouslyPurchasedCodes);
    const homeCode = String(user?.country || "").toLowerCase();
    const merged = new Set(
      [...base, ...previouslyPurchasedCodes].filter(
        (code) => !currentSelection.includes(code) || previous.has(code) || code === homeCode
      )
    );
    return [...merged];
  }, [purchase, previouslyPurchasedCodes, user?.country]);

  const selectableCountries = useMemo(() => {
    const blocked = new Set(accessibleCodes);
    return countries.filter((country) => {
      const code = String(country.code || "").toLowerCase();
      return code !== ALL_COUNTRIES_CODE && !blocked.has(code);
    });
  }, [accessibleCodes]);

  const filteredSelectableCountries = useMemo(() => {
    const normalizedSearch = String(countrySearchTerm || "").trim().toLowerCase();
    if (!normalizedSearch) return selectableCountries;
    return selectableCountries.filter((country) =>
      String(country.name || "").toLowerCase().includes(normalizedSearch)
      || String(country.code || "").toLowerCase().includes(normalizedSearch)
    );
  }, [countrySearchTerm, selectableCountries]);

  const refreshProfileAndContext = useCallback(async (latestPurchase = null) => {
    if (!user?.id) return;

    try {
      const profile = await getUserProfile(user.id);
      if (profile) {
        localStorage.setItem("user", JSON.stringify(profile));
      }
    } catch (error) {
      console.error("Erreur de rafraichissement du profil", error);
    }

    if (user?.country) {
      const homeCountry = countries.find((country) => country.code === user.country);
      const effectiveAccessibleCodes = latestPurchase
        ? getAccessibleCountryCodesFromPurchase(latestPurchase, user?.country)
        : getStoredAccessibleCountryCodes(user?.country);
      const currentSearchCountry = (() => {
        try {
          return JSON.parse(sessionStorage.getItem("search_country") || "null");
        } catch (error) {
          return null;
        }
      })();

      const currentCode = String(currentSearchCountry?.code || "").toLowerCase();
  const hasAccessToCurrent = currentCode && effectiveAccessibleCodes.includes(currentCode);

      if (!hasAccessToCurrent && homeCountry) {
        sessionStorage.setItem("search_country", JSON.stringify(homeCountry));
      }
    }

    sessionStorage.removeItem("search_igo");
  }, [user?.country, user?.id]);

  const refreshPurchasedCountries = useCallback(async () => {
    if (!user?.id) return;
    try {
      const response = await getPurchasedCountriesByUser(user.id);
      setPreviouslyPurchasedCodes(getPurchasedCountryCodesFromResponse(response));
    } catch (error) {
      console.error("Erreur de chargement des pays deja achetes", error);
    }
  }, [user?.id]);

  const hydrateFromPurchasePayload = useCallback(
    async (payload) => {
      const latestPurchase = getLatestCountryPurchase(payload);

      if (!latestPurchase) {
        setPurchase(null);
        setClientSecret("");
        return false;
      }

      setPurchase(latestPurchase);
      await refreshPurchasedCountries();
      setClientSecret(getClientSecretFromPurchase(latestPurchase));

      const purchaseStatus = getCountryPurchaseStatus(latestPurchase);
      applyCountryPurchaseAccess(user, latestPurchase);

      if (purchaseStatus === "completed") {
        applyServerSelectedCodes(getCountryPurchaseSelectedCodes(latestPurchase));
        setStatus(STATES.completed);
        await refreshProfileAndContext(latestPurchase);
        return true;
      }

      if (purchaseStatus === "paid") {
        applyServerSelectedCodes(getCountryPurchaseSelectedCodes(latestPurchase));
        setStatus(STATES.country_selection);
        return true;
      }

      if (purchaseStatus === "pending") {
        if (getClientSecretFromPurchase(latestPurchase)) {
          setStatus(STATES.payment_required);
        } else {
          setStatus(STATES.loading);
        }
        return true;
      }

      return false;
    },
    [applyServerSelectedCodes, refreshProfileAndContext, refreshPurchasedCountries, user]
  );

  const pollPurchaseStatus = useCallback(
    async ({ maxAttempts = 40, delayMs = 2500 } = {}) => {
      if (!user?.id) return;

      setStatus(STATES.waiting_for_webhook);
      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        try {
          const response = await getCountryPurchaseByUser(user.id);
          const latestPurchase = getLatestCountryPurchase(response);

          if (!latestPurchase) {
            await new Promise((resolve) => setTimeout(resolve, delayMs));
            continue;
          }

          setPurchase(latestPurchase);
          await refreshPurchasedCountries();
          applyCountryPurchaseAccess(user, latestPurchase);

          const purchaseStatus = getCountryPurchaseStatus(latestPurchase);
          if (purchaseStatus === "paid") {
            applyServerSelectedCodes(getCountryPurchaseSelectedCodes(latestPurchase));
            setStatus(STATES.country_selection);
            return;
          }

          if (purchaseStatus === "completed") {
            applyServerSelectedCodes(getCountryPurchaseSelectedCodes(latestPurchase));
            setStatus(STATES.completed);
            await refreshProfileAndContext(latestPurchase);
            return;
          }
        } catch (error) {
          console.error("Erreur polling achat pays", error);
        }

        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }

      setStatus(STATES.waiting_for_webhook);
      setErrorMessage("Paiement confirme cote client. En attente de validation serveur...");
    },
    [applyServerSelectedCodes, refreshProfileAndContext, refreshPurchasedCountries, user]
  );

  useEffect(() => {
    const init = async () => {
      if (!user?.id) {
        setStatus(STATES.error);
        setErrorMessage("Utilisateur introuvable. Merci de vous reconnecter.");
        return;
      }

      setStatus(STATES.loading);
      setErrorMessage("");

      try {
        const [pricesResponse, stripeResponse, purchaseResponse] = await Promise.all([
          getCountryPurchasePrices("eur"),
          getPublicKeyStripe(),
          getCountryPurchaseByUser(user.id),
        ]);

        const availablePrices = Array.isArray(pricesResponse?.data)
          ? pricesResponse.data
          : Array.isArray(pricesResponse)
          ? pricesResponse
          : [];

        setPrices(availablePrices);
        if (availablePrices.length > 0) {
          setSelectedPriceId(availablePrices[0].id);
        }

        setStripeKey(stripeResponse?.value || "");

        const applied = await hydrateFromPurchasePayload(purchaseResponse);
        if (!applied) {
          setStatus(STATES.payment_required);
        }
      } catch (error) {
        setStatus(STATES.error);
        setErrorMessage(getErrorMessage(error, "Impossible de charger le parcours d'achat."));
      }
    };

    init();
  }, [hydrateFromPurchasePayload, user]);

  useEffect(() => {
    const hasStripeRedirectParam = Boolean(
      new URLSearchParams(window.location.search).get("payment_intent_client_secret")
    );

    if (hasStripeRedirectParam) {
      pollPurchaseStatus();
    }
  }, [pollPurchaseStatus]);

  const createOrder = async () => {
    if (!selectedPrice || !user?.id) return;

    setErrorMessage("");
    setStatus(STATES.loading);

    try {
      const response = await createCountryPurchase(Number(selectedPrice.id), Number(user.id));
      const latestPurchase = getLatestCountryPurchase(response);

      if (!latestPurchase) {
        throw new Error("Commande non creee. Merci de reessayer.");
      }

      setPurchase(latestPurchase);
      await refreshPurchasedCountries();
      setClientSecret(getClientSecretFromPurchase(latestPurchase));

      const purchaseStatus = getCountryPurchaseStatus(latestPurchase);
      if (purchaseStatus === "paid") {
        setStatus(STATES.country_selection);
        return;
      }

      if (!getClientSecretFromPurchase(latestPurchase)) {
        throw new Error("Client secret introuvable pour le paiement.");
      }

      setStatus(STATES.payment_required);
    } catch (error) {
      setStatus(STATES.error);
      setErrorMessage(getErrorMessage(error, "Impossible de creer la commande."));
    }
  };

  const toggleCountry = (countryCode) => {
    const normalizedCode = normalizeCountryCode(countryCode);
    if (normalizedCode === ALL_COUNTRIES_CODE) return;

    setSelectedCountryCodes((previous) => {
      if (previous.includes(normalizedCode)) {
        return previous.filter((code) => code !== normalizedCode);
      }
      return [...previous, normalizedCode];
    });
  };

  const submitCountrySelection = async () => {
    if (!purchase || !user?.id) return;

    const countryCodes = selectedCountryCodes.filter((code) => code !== ALL_COUNTRIES_CODE);
    const purchaseId = getCountryPurchaseId(purchase);
    if (!purchaseId) {
      setErrorMessage("Commande invalide. Merci de relancer l'achat.");
      return;
    }

    if (countryCodes.length !== countryCount) {
      setErrorMessage(
        `Le nombre de pays selectionnes doit etre exactement ${countryCount}.`
      );
      return;
    }

    setIsSubmittingSelection(true);
    setErrorMessage("");

    try {
      await selectCountryPurchaseCountries(purchaseId, Number(user.id), countryCodes);
      await pollPurchaseStatus({ maxAttempts: 25, delayMs: 2000 });
    } catch (error) {
      setStatus(STATES.country_selection);
      setErrorMessage(
        getErrorMessage(
          error,
          "Selection invalide: un pays est deja accessible ou la formule ne correspond pas."
        )
      );
    } finally {
      setIsSubmittingSelection(false);
    }
  };

  const headline = {
    [STATES.loading]: "Chargement du parcours d'achat",
    [STATES.payment_required]: "Paiement requis",
    [STATES.payment_processing]: "Paiement en cours",
    [STATES.waiting_for_webhook]: "Validation serveur en cours",
    [STATES.country_selection]: "Selection des pays",
    [STATES.error]: "Erreur",
  };

  return (
    <section className="max-w-5xl mx-auto px-4 py-8">
      <header className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Ajouter des pays</h1>
        <p className="text-gray-600 mt-2">
          Premium (hors IA) - Ajoutez des pays
        </p>
      </header>

      {status !== STATES.completed && (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900">{headline[status]}</h2>
          {errorMessage && (
            <p className="mt-3 rounded-lg border border-red-200 bg-red-50 text-red-700 px-3 py-2">
              {errorMessage}
            </p>
          )}
        </div>
      )}

      <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-6 h-[300px] overflow-y-scroll">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Votre commande</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-5">
          {prices.map((price) => {
            const active = Number(selectedPriceId) === Number(price.id);
            const priceCountryCount = Number(
              price.countryCount ?? price.country_count ?? price.count ?? 0
            );
            return (
              <button
                key={price.id}
                type="button"
                onClick={() => setSelectedPriceId(price.id)}
                disabled={status === STATES.payment_processing || status === STATES.waiting_for_webhook || isSubmittingSelection}
                className={`text-left rounded-lg border px-4 py-3 transition ${
                  active ? "border-[#00739d] bg-cyan-50" : "border-gray-200 hover:border-[#00739d]"
                }`}
              >
                <p className="font-semibold text-gray-900">{price.title || `${priceCountryCount} pays`}</p>
                <p className="text-sm text-gray-600 mt-1">{priceCountryCount} pays</p>
                <p className="text-lg font-bold text-gray-900 mt-2">
                  {getDisplayPrice(price)}
                </p>
              </button>
            );
          })}
        </div>

        <button
          type="button"
          onClick={createOrder}
          disabled={!selectedPrice || status === STATES.payment_processing || status === STATES.waiting_for_webhook || isSubmittingSelection}
          className="px-5 py-3 rounded-lg text-white font-semibold bg-[#00739d] hover:bg-[#005d7f] disabled:bg-gray-400"
        >
          Confirmer la commande
        </button>

        {(status === STATES.payment_required || status === STATES.payment_processing) && (
          <div className="mt-8">
            <h3 className="text-lg font-semibold text-gray-900">2. Regler le paiement</h3>
            <CountryPaymentElement
              clientSecret={clientSecret}
              stripePromise={stripePromise}
              disabled={status === STATES.payment_processing || status === STATES.waiting_for_webhook}
              onStart={() => setStatus(STATES.payment_processing)}
              onSuccess={() => pollPurchaseStatus()}
              onFailure={(message) => {
                setStatus(STATES.payment_required);
                setErrorMessage(message);
              }}
            />
          </div>
        )}

        {status === STATES.waiting_for_webhook && (
          <div className="mt-8 rounded-lg border border-cyan-200 bg-cyan-50 px-4 py-3 text-cyan-800">
            Paiement confirme cote client. Nous attendons la validation backend (webhook Stripe)...
          </div>
        )}

        {status === STATES.country_selection && (
          <div className="mt-8">
            <h3 className="text-lg font-semibold text-gray-900">3. Selectionner vos pays</h3>
            <p className="text-sm text-gray-600 mt-1">
              Selection obligatoire: {countryCount} pays exactement. Pays deja accessibles exclus.
            </p>

            <input
              type="text"
              value={countrySearchTerm}
              onChange={(event) => setCountrySearchTerm(event.target.value)}
              placeholder="Rechercher un pays..."
              className="mt-4 w-full max-w-md border border-gray-300 rounded-lg px-3 py-2"
            />

            <div className="flex flex-wrap gap-2 mt-4">
              {filteredSelectableCountries.map((country) => {
                const countryCode = normalizeCountryCode(country.code);
                const checked = selectedCountryCodes.includes(countryCode);
                const hasReachedSelectionLimit = countryCount > 0 && selectedCountryCodes.length >= countryCount;
                return (
                  <button
                    key={country.code}
                    type="button"
                    onClick={() => toggleCountry(countryCode)}
                    disabled={
                      isSubmittingSelection
                      || status === STATES.payment_processing
                      || status === STATES.waiting_for_webhook
                      || hasReachedSelectionLimit
                    }
                    className={`px-3 py-2 rounded-full border text-sm ${
                      checked
                        ? "bg-[#00739d] border-[#00739d] text-white"
                        : "bg-white border-gray-300 text-gray-700"
                    }`}
                  >
                    {country.name}
                  </button>
                );
              })}
            </div>

            {filteredSelectableCountries.length === 0 && (
              <p className="mt-3 text-sm text-gray-500">Aucun pays ne correspond a cette recherche.</p>
            )}

            <button
              type="button"
              onClick={submitCountrySelection}
              disabled={
                isSubmittingSelection ||
                status === STATES.payment_processing ||
                status === STATES.waiting_for_webhook ||
                selectedCountryCodes.length !== countryCount
              }
              className="mt-4 px-5 py-3 rounded-lg text-white font-semibold bg-[#00739d] hover:bg-[#005d7f] disabled:bg-gray-400"
            >
              {isSubmittingSelection ? "Validation..." : "Valider mes pays"}
            </button>
          </div>
        )}

        {status === STATES.completed && (
          <div className="mt-8 rounded-lg border border-green-200 bg-green-50 px-4 py-4">
            <p className="text-green-800 font-semibold">
              Commande confirmée. Accès aux pays ouverts
            </p>
            <div className="flex flex-wrap gap-3 mt-4">
              <button
                type="button"
                className="px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700"
                onClick={() => {
                  window.location.href = "/igo";
                }}
              >
                Accéder aux Forums Globaux
              </button>
              <button
                type="button"
                className="px-4 py-2 rounded-lg border border-green-600 text-green-700 hover:bg-green-100"
                onClick={() => {
                  window.location.href = "/";
                }}
              >
                Retour à l’accueil
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
